<script setup lang="ts">
// PROPS
interface Props {
    selectedProposal: number;
    counterPartReferenceProposal: number;
    counterPartCurrentProposal?: number;
    isBuyer: boolean;
}

const { selectedProposal, counterPartReferenceProposal, counterPartCurrentProposal, isBuyer } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
const acceptanceProbability = computed(() => {
    if (selectedProposal === counterPartCurrentProposal) return 100;

    return isBuyer
        ? getBuyerSideAcceptanceProbability(selectedProposal, counterPartReferenceProposal)
        : getSellerSideAcceptanceProbability(selectedProposal, counterPartReferenceProposal);
});

type ProbabilityLevel = 'guaranteed' | 'veryHigh' | 'high' | 'mediumHigh' | 'medium' | 'mediumLow' | 'low';

const probabilityLevel = computed<ProbabilityLevel>(() => {
    const p = acceptanceProbability.value;

    if (p >= 100) return 'guaranteed';

    if (isBuyer) {
        if (p >= 85) return 'veryHigh';
        if (p >= 75) return 'high';
        if (p >= 62) return 'mediumHigh';
        if (p >= 57) return 'medium';
        if (p >= 50) return 'mediumLow';
    } else {
        if (p >= 90) return 'veryHigh';
        if (p >= 76) return 'high';
        if (p >= 64) return 'mediumHigh';
        if (p >= 57) return 'medium';
        if (p >= 50) return 'mediumLow';
    }

    return 'low';
});

const title = computed(() =>
    tt(`negotiation.acceptanceProbability.${probabilityLevel.value}`, {
        probability: Math.round(acceptanceProbability.value),
    })
);

const barColor = computed<string>(() => {
    const map: Record<ProbabilityLevel, string> = {
        guaranteed: 'bg-green-700',
        veryHigh: 'bg-green-600',
        high: 'bg-green-400',
        mediumHigh: 'bg-lime-400',
        medium: 'bg-yellow-400',
        mediumLow: 'bg-amber-500',
        low: 'bg-red-500',
    };

    return map[probabilityLevel.value];
});

// METHODS

/**
 * Buyer-side: piecewise linear approximation of the offer acceptance probability.
 * Input: buyer's offer amount vs. seller's reference request.
 * Higher ratio (offer / request) → higher probability.
 */
function getBuyerSideAcceptanceProbability(selectedOffer: number, referenceRequest: number): number {
    if (referenceRequest <= 0) {
        return 0;
    }

    if (selectedOffer >= referenceRequest) {
        return 100;
    }

    const ratio = selectedOffer / referenceRequest;

    let probability: number;

    if (ratio >= 0.91) {
        probability = 164.33 * ratio - 64.33;
    } else if (ratio >= 0.76) {
        probability = 128.4 * ratio - 31.634;
    } else if (ratio >= 0.62) {
        probability = 108.93 * ratio - 16.837;
    } else {
        probability = 185 * ratio - 64;
    }

    return Math.min(100, Math.max(1, probability));
}

/**
 * Seller-side: piecewise linear approximation of the request acceptance probability.
 * Input: seller's request amount vs. the buyer's reference offer.
 * Higher ratio (request / offer) → lower probability.
 */
function getSellerSideAcceptanceProbability(selectedProposal: number, counterPartReferenceProposal: number): number {
    if (counterPartReferenceProposal <= 0) {
        return 0;
    }

    if (selectedProposal <= counterPartReferenceProposal) {
        return 100;
    }

    const ratio = selectedProposal / counterPartReferenceProposal;

    let probability: number;

    if (ratio <= 1.02) {
        probability = 210 - 110 * ratio;
    } else if (ratio <= 1.05) {
        probability = 426.92 - 322.67 * ratio;
    } else if (ratio <= 1.09) {
        probability = 493.95 - 386.5 * ratio;
    } else if (ratio <= 1.15) {
        probability = 479.05 - 372.83 * ratio;
    } else {
        probability = 932.57 - 767.2 * ratio;
    }

    return Math.min(100, Math.max(1, probability));
}
</script>

<template>
    <div class="pb-4">
        <p class="text-highlighted text-sm font-medium">{{ title }}</p>
        <UProgress class="mt-2" :model-value="acceptanceProbability" :ui="{ indicator: barColor }" />
    </div>
</template>
